// Brick registry and host. The schema (props, selectors, anchors) lives in schema.ts so the
// validator can read it without React; this file maps each brick name to its component.

import type { BrickInstance } from '../presentation/types';
import { useScene, seatsFor } from '../scene/SceneContext';
import { ActionTiles, Hud, LogFeed, OpponentStrip, Panel, ResultPodium, SeatMat } from './structure';
import { CardGrid, HandFan, MarketRow, ObjectiveList, Pile, Tableau } from './cards';
import { RouteMap } from './RouteMap';
import { ComplexBoard } from './ComplexBoard';
import { CollectionEditor } from './CollectionEditor';
import { PartitionBoard } from './PartitionBoard';
import { ReferencePanel } from './ReferencePanel';
import { RulesGuide } from './RulesGuide';
import { PiecePool, ResourceTray, SpaceBoard, Track } from './spaces';
import { CatalogCall, CallRuleCard, RuleStrip, TableDealPrompt } from '../catalog/CatalogCall';
import { SeatRing } from '../boards/seatRing/SeatRing';
import { ExactMoneyAction } from '../catalog/ExactMoneyAction';
import { SettlementCard } from '../catalog/SettlementCard';
import { ObjectInspector } from './ObjectInspector';

type BrickComponent = (props: { inst: BrickInstance; seat: string | null }) => React.ReactNode;

export const REGISTRY: Record<string, BrickComponent> = {
  'catalog-call': CatalogCall, 'call-rule-card': CallRuleCard, 'table-deal-prompt': TableDealPrompt, 'rule-strip': RuleStrip, 'seat-ring': SeatRing,
  'exact-money-action': ExactMoneyAction, 'settlement-card': SettlementCard,
  'partition-board': PartitionBoard,
  'collection-editor': CollectionEditor,
  'reference-panel': ReferencePanel,
  'rules-guide': RulesGuide,
  hud: Hud, panel: Panel, 'seat-mat': SeatMat, 'opponent-strip': OpponentStrip, 'log-feed': LogFeed, 'result-podium': ResultPodium, 'action-tiles': ActionTiles,
  'hand-fan': HandFan, 'market-row': MarketRow, 'card-grid': CardGrid, tableau: Tableau, pile: Pile, 'objective-list': ObjectiveList,
  'object-inspector': ObjectInspector,
  'route-map': RouteMap, 'complex-board': ComplexBoard, 'space-board': SpaceBoard, track: Track, 'resource-tray': ResourceTray, 'piece-pool': PiecePool,
  // Overlays are placed by the client; a scene may list them only to set props.
  'action-bar': () => null, 'moves-drawer': () => null, 'turn-banner': () => null,
};

/** Renders one brick instance, expanding `repeat` over seats. */
export function BrickHost({ inst, seat }: { inst: BrickInstance; seat: string | null }) {
  const { table } = useScene();
  if (inst.brick === 'repeat') {
    if (!inst.child) return null;
    return <>{seatsFor(inst, table.seats, table).map(s => <BrickHost key={s.id} inst={inst.child!} seat={s.id} />)}</>;
  }
  const Component = REGISTRY[inst.brick];
  return Component ? <Component inst={inst} seat={seat} /> : null;
}
